import { useMemo, useEffect, useCallback, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

export const useAbortSignal = () => {
  const controller = useMemo(() => new AbortController(), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => controller.abort("Cancelled"), []);
  return controller.signal; // returns a boolean
};

export const useNavigateCompat = () => {
  const navigate = useNavigate();

  return useCallback(
    (to, options = {}) => {
      const { replace = false, state } = options;

      if (typeof to === "number") {
        navigate(to);
        return;
      }

      if (typeof to === "string") {
        navigate(to, { replace, state });
        return;
      }

      const locationDescriptor = { ...(to || {}) };
      if (state !== undefined) {
        locationDescriptor.state = state;
      }

      navigate(locationDescriptor, { replace, state: locationDescriptor.state });
    },
    [navigate]
  );
};

const identity = value => value;

// Parse every configured key out of a URLSearchParams into a plain values object,
// applying each key's `parse` (or identity) and falling back to `defaultValue`
// when the param is absent.
const parseUrlState = (searchParams, config) => {
  const result = {};
  for (const [key, parser] of Object.entries(config)) {
    const raw = searchParams.get(key);
    const parse = parser.parse ?? identity;
    result[key] = raw === null ? (parser.defaultValue ?? null) : parse(raw);
  }
  return result;
};

/**
 * useUrlState — read and write a set of named values to the URL query string,
 * with a typed parse/serialize per key and batched, composable multi-key writes.
 *
 * Why this exists
 * ---------------
 * Persisting "view state" (search, sort, page, selected tab) means choosing
 * where it lives based on how long it must outlive the component:
 *   • URL query params (this hook): survives a true page load, is restored by
 *     Back/Forward, and is shareable by copy-pasting the link. The default for
 *     list/table view-state and anything a user might bookmark.
 *   • sessionStorage / localStorage: survives a reload but is private and
 *     meaningless in a shared link (dismissed banners, draft text).
 *   • useState / in-memory: ephemeral UI state with none of those properties.
 *
 * API (mirrors nuqs' useQueryStates, so a future swap to that library is
 * mechanical):
 *
 *   const [state, setState] = useUrlState({
 *     q:    { defaultValue: "" },
 *     sort: { parse: parseSort, serialize: formatSort, defaultValue: null },
 *     page: { parse: parsePage, serialize: formatPage, defaultValue: 1 },
 *   });
 *
 *   setState({ q: "abc" });             // merge a single key
 *   setState({ q: "abc", page: null }); // batched: one history entry
 *   setState(prev => ({ page: prev.page + 1 })); // useState-style updater
 *
 * Each config entry is `{ parse?, serialize?, defaultValue? }`. `parse` turns the
 * raw URL string into the value the component wants (default: identity);
 * `serialize` turns a value back into a string (default: String). A key is
 * removed from the URL when its value — or its serialized form — is
 * null/undefined/"", so default/empty values keep the URL clean.
 *
 * `config` is expected to be stable across renders (define it at module scope or
 * memoize it): parsed values are recomputed only when the URL changes, so a
 * config whose parsers/defaults change in place won't take effect until the next
 * write — same expectation as nuqs.
 *
 * Writes use { replace: true } so typing doesn't flood the history stack, and
 * several writes dispatched in the same React tick compose instead of clobbering
 * each other (see pendingRef below).
 */
export const useUrlState = config => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Hold the latest config and committed params in refs so `setUrlState` keeps a
  // stable identity (it depends only on `navigate`) while always reading current
  // values.
  const configRef = useRef(config);
  configRef.current = config;
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const values = useMemo(() => parseUrlState(searchParams, configRef.current), [searchParams]);

  // NOTE: react-router derives the next query string from render-time params, so
  // two writes in the same tick would each start from the same base and the later
  // one would drop the earlier one's change — e.g. react-table's autoResetPageIndex
  // clearing `page` the instant the user edits `q`. We accumulate intra-tick writes
  // here and clear once the committed URL catches up.
  const pendingRef = useRef(null);
  useEffect(() => {
    pendingRef.current = null;
  }, [searchParams]);

  const setUrlState = useCallback(
    updates => {
      const activeConfig = configRef.current;
      const params = pendingRef.current ?? new URLSearchParams(searchParamsRef.current);
      const resolved = typeof updates === "function" ? updates(parseUrlState(params, activeConfig)) : updates;
      for (const [key, value] of Object.entries(resolved)) {
        const serialize = activeConfig[key]?.serialize ?? String;
        const serialized = value === null || value === undefined ? null : serialize(value);
        if (serialized === null || serialized === undefined || serialized === "") {
          params.delete(key);
        } else {
          params.set(key, serialized);
        }
      }
      pendingRef.current = params;
      navigate({ search: params.toString() ? `?${params.toString()}` : "" }, { replace: true });
    },
    [navigate]
  );

  return [values, setUrlState];
};

export const useDebouncedValue = (value, delay = 250) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timeoutId);
  }, [value, delay]);

  return debouncedValue;
};
